<script>
import NextButton from 'dashboard/components-next/button/Button.vue';
import TagInput from 'dashboard/components-next/taginput/TagInput.vue';

export default {
  name: 'FilterInput',
  components: {
    NextButton,
    TagInput,
  },
  props: {
    modelValue: {
      type: Object,
      default: () => {},
    },
    filterAttributes: {
      type: Array,
      default: () => [],
    },
    inputType: {
      type: String,
      default: 'plain_text',
    },
    operators: {
      type: Array,
      default: () => [],
    },
    dropdownValues: {
      type: Array,
      default: () => [],
    },
    showQueryOperator: {
      type: Boolean,
      default: false,
    },
    showUserInput: {
      type: Boolean,
      default: true,
    },
    groupedFilters: {
      type: Boolean,
      default: false,
    },
    filterGroups: {
      type: Array,
      default: () => [],
    },
    customAttributeType: {
      type: String,
      default: '',
    },
    errorMessage: {
      type: String,
      default: '',
    },
  },
  emits: ['update:modelValue', 'removeFilter', 'resetFilter'],
  data() {
    return {
      idleAmount: '',
      idleUnit: 'hours',
    };
  },
  computed: {
    attributeKey: {
      get() {
        if (!this.modelValue) return null;
        return this.modelValue.attribute_key;
      },
      set(value) {
        const payload = this.modelValue || {};
        this.$emit('update:modelValue', { ...payload, attribute_key: value });
      },
    },
    filterOperator: {
      get() {
        if (!this.modelValue) return null;
        return this.modelValue.filter_operator;
      },
      set(value) {
        const payload = this.modelValue || {};
        this.$emit('update:modelValue', { ...payload, filter_operator: value });
      },
    },
    values: {
      get() {
        if (!this.modelValue) return null;
        return this.modelValue.values;
      },
      set(value) {
        const payload = this.modelValue || {};
        this.$emit('update:modelValue', { ...payload, values: value });
      },
    },
    query_operator: {
      get() {
        if (!this.modelValue) return null;
        return this.modelValue.query_operator;
      },
      set(value) {
        const payload = this.modelValue || {};
        this.$emit('update:modelValue', { ...payload, query_operator: value });
      },
    },
    custom_attribute_type: {
      get() {
        if (!this.customAttributeType) return '';
        return this.customAttributeType;
      },
      set() {
        const payload = this.modelValue || {};
        this.$emit('update:modelValue', {
          ...payload,
          custom_attribute_type: this.customAttributeType,
        });
      },
    },
    inputPlaceholder() {
      if (
        this.attributeKey === 'content' &&
        this.filterOperator === 'contains'
      ) {
        return this.$t('AUTOMATION.CONDITION.MESSAGE_KEYWORDS_PLACEHOLDER');
      }

      return this.$t('FILTER.INPUT_PLACEHOLDER');
    },
    messageKeywords: {
      get() {
        return (this.values || '')
          .split(',')
          .map(value => value.trim())
          .filter(Boolean);
      },
      set(keywords) {
        this.values = keywords.join(', ');
      },
    },
    showKeywordHelp() {
      return (
        this.attributeKey === 'content' && this.filterOperator === 'contains'
      );
    },
  },
  watch: {
    operators: {
      handler(value) {
        if (value.length === 1) {
          this.filterOperator = value[0].value;
        }
      },
      immediate: true,
    },
    idleAmount(val) {
      this.emitIdleHours(val, this.idleUnit);
    },
    idleUnit(val) {
      this.emitIdleHours(this.idleAmount, val);
    },
    customAttributeType: {
      handler(value) {
        if (
          value === 'conversation_attribute' ||
          value === 'contact_attribute'
        ) {
          // eslint-disable-next-line vue/no-mutating-props
          this.modelValue.custom_attribute_type = this.customAttributeType;
          // eslint-disable-next-line vue/no-mutating-props
        } else this.modelValue.custom_attribute_type = '';
      },
      immediate: true,
    },
  },
  methods: {
    emitIdleHours(amount, unit) {
      const n = parseFloat(amount);
      if (Number.isNaN(n) || n < 0) return;
      let hours = n;
      if (unit === 'minutes') hours = n / 60;
      else if (unit === 'days') hours = n * 24;
      this.values = [hours];
    },
    removeFilter() {
      this.$emit('removeFilter');
    },
    resetFilter() {
      this.$emit('resetFilter');
    },
    getInputErrorClass(errorMessage) {
      return errorMessage
        ? 'bg-n-ruby-8/20 border-n-ruby-5 dark:border-n-ruby-5'
        : 'bg-n-background border-n-weak dark:border-n-weak';
    },
    getOperatorLabel(operator) {
      if (this.attributeKey === 'content' && operator.value === 'contains') {
        return this.$t('AUTOMATION.CONDITION.MESSAGE_CONTAINS_ANY');
      }

      return this.$t(`FILTER.OPERATOR_LABELS.${operator.value}`);
    },
  },
};
</script>

<!-- eslint-disable vue/no-mutating-props -->
<template>
  <div>
    <div
      class="p-2 border border-solid rounded-lg"
      :class="getInputErrorClass(errorMessage)"
    >
      <div class="flex gap-1">
        <select
          v-if="groupedFilters"
          v-model="attributeKey"
          class="max-w-[30%] mb-0 mr-1"
          @change="resetFilter()"
        >
          <optgroup
            v-for="(group, i) in filterGroups"
            :key="i"
            :label="group.name"
          >
            <option
              v-for="attribute in group.attributes"
              :key="attribute.key"
              :value="attribute.key"
              :selected="true"
            >
              {{ attribute.name }}
            </option>
          </optgroup>
        </select>
        <select
          v-else
          v-model="attributeKey"
          class="max-w-[30%] mb-0 mr-1"
          @change="resetFilter()"
        >
          <option
            v-for="attribute in filterAttributes"
            :key="attribute.key"
            :value="attribute.key"
            :disabled="attribute.disabled"
          >
            {{ attribute.name }}
          </option>
        </select>

        <select
          v-if="operators.length > 1"
          v-model="filterOperator"
          class="max-w-[20%] mb-0 mr-1"
        >
          <option
            v-for="(operator, o) in operators"
            :key="o"
            :value="operator.value"
          >
            {{ getOperatorLabel(operator) }}
          </option>
        </select>

        <div v-if="showUserInput" class="flex-grow mr-1 filter__answer--wrap">
          <div
            v-if="inputType === 'multi_select'"
            class="multiselect-wrap--small"
          >
            <multiselect
              v-model="values"
              track-by="id"
              label="name"
              :placeholder="$t('FORMS.MULTISELECT.SELECT')"
              multiple
              selected-label
              :select-label="$t('FORMS.MULTISELECT.ENTER_TO_SELECT')"
              deselect-label=""
              :max-height="160"
              :options="dropdownValues"
              :allow-empty="false"
            >
              <template #noOptions>
                {{ $t('FORMS.MULTISELECT.NO_OPTIONS') }}
              </template>
            </multiselect>
          </div>
          <div
            v-else-if="inputType === 'search_select'"
            class="multiselect-wrap--small"
          >
            <multiselect
              v-model="values"
              track-by="id"
              label="name"
              :placeholder="$t('FORMS.MULTISELECT.SELECT')"
              selected-label
              :select-label="$t('FORMS.MULTISELECT.ENTER_TO_SELECT')"
              deselect-label=""
              :max-height="160"
              :options="dropdownValues"
              :allow-empty="false"
              :option-height="104"
            >
              <template #noOptions>
                {{ $t('FORMS.MULTISELECT.NO_OPTIONS') }}
              </template>
            </multiselect>
          </div>
          <div v-else-if="inputType === 'date'" class="multiselect-wrap--small">
            <input
              v-model="values"
              type="date"
              :editable="false"
              class="!mb-0 datepicker"
            />
          </div>
          <div v-else-if="filterOperator === 'hours_before'" class="flex gap-1">
            <input
              v-model="idleAmount"
              type="number"
              min="0"
              step="1"
              class="!mb-0 w-20 hours-input"
              :placeholder="$t('FILTER.IDLE_AMOUNT_PLACEHOLDER')"
            />
            <select v-model="idleUnit" class="!mb-0">
              <option value="minutes">
                {{ $t('FILTER.UNITS.MINUTES') }}
              </option>
              <option value="hours">
                {{ $t('FILTER.UNITS.HOURS') }}
              </option>
              <option value="days">
                {{ $t('FILTER.UNITS.DAYS') }}
              </option>
            </select>
          </div>
          <div
            v-else-if="inputType === 'comma_separated_plain_text'"
            class="w-full"
          >
            <TagInput
              v-model="messageKeywords"
              :placeholder="inputPlaceholder"
            />
            <p v-if="showKeywordHelp" class="mt-1 mb-0 text-xs text-n-slate-9">
              {{ $t('AUTOMATION.CONDITION.MESSAGE_KEYWORDS_HELP') }}
            </p>
          </div>
          <input
            v-else
            v-model="values"
            type="text"
            class="!mb-0"
            :placeholder="inputPlaceholder"
          />
        </div>
        <NextButton
          icon="i-lucide-x"
          slate
          ghost
          class="flex-shrink-0"
          @click="removeFilter"
        />
      </div>
      <p v-if="errorMessage" class="filter-error">
        {{ errorMessage }}
      </p>
    </div>

    <div
      v-if="showQueryOperator"
      class="flex items-center justify-center relative my-2.5 mx-0"
    >
      <hr class="absolute w-full border-b border-solid border-n-weak" />
      <select
        v-model="query_operator"
        class="relative w-auto mb-0 bg-n-background text-n-slate-12 border-n-weak"
      >
        <option value="and">
          {{ $t('FILTER.QUERY_DROPDOWN_LABELS.AND') }}
        </option>
        <option value="or">
          {{ $t('FILTER.QUERY_DROPDOWN_LABELS.OR') }}
        </option>
      </select>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.filter__answer--wrap {
  input {
    @apply bg-n-background mb-0 text-n-slate-12 border-n-weak;
  }
}

.filter-error {
  @apply text-n-ruby-9 dark:text-n-ruby-9 block my-1 mx-0;
}

.multiselect {
  @apply mb-0;
}

.hours-input::-webkit-outer-spin-button,
.hours-input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

.hours-input {
  -moz-appearance: textfield;
}
</style>
